import { useEffect, useRef } from 'react';
import 'leaflet/dist/leaflet.css';
import '../styles/Map.css';

const locationCopy = {
  swe: {
    kicker: 'Var vi finns',
    title: 'Vi sitter i Sveriges vackraste stad Jönköping',
    text:
      'Välkommen till oss i Jönköping. Härifrån stöttar vi kunder i hela Sverige med snabb och personlig leverans.',
    addressTitle: 'Besöksadress',
    addressLine1: 'Fridhemsvägen 29',
    addressLine2: '553 02 Jönköping',
    addressLine3: 'Sverige',
    ctaPrimary: 'Skicka förfrågan',
    ctaSecondary: 'Öppna i karta',
    badge: 'OpenStreetMap'
  },
  eng: {
    kicker: 'Our location',
    title: 'We are based in beautiful Jönköping, Sweden',
    text:
      'From Jönköping we support clients across Sweden with fast and personal delivery.',
    addressTitle: 'Office address',
    addressLine1: 'Fridhemsvägen 29',
    addressLine2: '553 02 Jönköping',
    addressLine3: 'Sweden',
    ctaPrimary: 'Send request',
    ctaSecondary: 'Open in map',
    badge: 'OpenStreetMap'
  }
};

const mapOpenUrl =
  'https://www.openstreetmap.org/?mlat=57.78202&mlon=14.16884#map=15/57.78202/14.16884';

function Map({ lang = 'swe', setPage }) {
  const text = locationCopy[lang] || locationCopy.swe;
  const mapElementRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (process.env.NODE_ENV === 'test') {
      return undefined;
    }

    let disposed = false;

    const initializeMap = async () => {
      if (!mapElementRef.current || mapInstanceRef.current) {
        return;
      }

      const leafletModule = await import('leaflet');
      const L = leafletModule.default || leafletModule;

      if (disposed || !mapElementRef.current) {
        return;
      }

      const map = L.map(mapElementRef.current, {
        center: [57.78202, 14.16884],
        zoom: 15,
        scrollWheelZoom: false,
        dragging: false,
        touchZoom: false,
        doubleClickZoom: false,
        boxZoom: false,
        keyboard: false,
        tap: false,
        zoomControl: true
      });

      // Extra hard lock for mobile browsers (Leaflet handlers can differ by device)
      map.scrollWheelZoom.disable();
      map.dragging.disable();
      map.touchZoom.disable();
      map.doubleClickZoom.disable();
      map.boxZoom.disable();
      map.keyboard.disable();
      map.off('click');
      if (map.tap) {
        map.tap.disable();
      }

      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors'
      }).addTo(map);

      L.circleMarker([57.78202, 14.16884], {
        radius: 8,
        color: '#1d4ed8',
        fillColor: '#2563eb',
        fillOpacity: 0.88,
        weight: 2
      })
        .addTo(map)
        .bindPopup('Fridhemsvägen 29, 553 02 Jönköping, Sweden');

      mapInstanceRef.current = map;
    };

    initializeMap();

    return () => {
      disposed = true;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <section className="map-cta">
      <div className="map-copy">
        <p className="map-kicker">{text.kicker}</p>
        <h2>{text.title}</h2>
        <p className="map-text">{text.text}</p>

        <div className="map-address">
          <h3>{text.addressTitle}</h3>
          <p>{text.addressLine1}</p>
          <p>{text.addressLine2}</p>
          <p>{text.addressLine3}</p>
        </div>

        <div className="map-actions">
          <button type="button" className="map-primary-btn" onClick={() => setPage('contact')}>
            {text.ctaPrimary}
          </button>
          <a href={mapOpenUrl} target="_blank" rel="noreferrer" className="map-link-btn">
            {text.ctaSecondary}
          </a>
        </div>
      </div>

      <div className="map-frame-wrap">
        <span className="map-badge">{text.badge}</span>
        <div className="map-leaflet" ref={mapElementRef} aria-label="Execora location map" />
      </div>
    </section>
  );
}

export default Map;
